import { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import NavInf from '../../components/NavInf/NavInf';
import ProductWrapper from '../../components/Product/ProductWrapper';
import { ClipLoader } from 'react-spinners';
import throttle from 'lodash.throttle';
import Masonry from 'react-masonry-css';
import './Home.css';
import EnableNotifications from '../../components/EnableNotifications/EnableNotifications';
import WhatsAppBtn from '../../components/WhatsAppBtn/WhatsAppBtn.js';
import Reels from '../../components/Reels/Reels.js';
import AutoCarousel from '../../components/AutoCarousel/AutoCarousel.js';

function shuffleArray(arr) {
  return arr
    .map(value => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);
}

const ITEMS_PER_LOAD = 10;

function Home() {
  const location = useLocation();
  const [productIds, setProductIds] = useState([]); // Array de IDs shuffled
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadIndexRef = useRef(0);
  const [hasMore, setHasMore] = useState(true);
  const lastLocationRef = useRef(location.pathname);

  // Configuración de breakpoints para Masonry
  const breakpointColumnsObj = {
    default: 6,
    1600: 6,
    1200: 5,
    768: 3,
    480: 2
  };

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || productIds.length === 0) return;
    setLoadingMore(true);

    try {
      // Obtener los siguientes IDs del array shuffled
      const nextIds = productIds.slice(
        loadIndexRef.current,
        loadIndexRef.current + ITEMS_PER_LOAD
      );

      if (nextIds.length === 0) {
        setHasMore(false);
        setLoadingMore(false);
        return;
      }

      // Cargar previews solo de estos IDs
      const previewsRes = await fetch('https://api.minymol.com/products/previews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: nextIds }),
      });

      if (!previewsRes.ok) {
        throw new Error(`HTTP ${previewsRes.status}`);
      }

      const previews = await previewsRes.json();
      
      setVisibleProducts(prev => [...prev, ...previews]);
      loadIndexRef.current += ITEMS_PER_LOAD;
      
      if (loadIndexRef.current >= productIds.length) {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error al cargar más productos:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [productIds, loadingMore, hasMore]);

  const handleScroll = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;

    if (
      window.innerHeight +
      document.documentElement.scrollTop +
      200 >=
      document.documentElement.offsetHeight
    ) {
      loadMore();
    }
  }, [loading, loadingMore, hasMore, loadMore]);

  useEffect(() => {
    const fetchProductos = async () => {
      try {
        setLoading(true);
        setVisibleProducts([]);
        loadIndexRef.current = 0;
        setHasMore(true);

        // Traer solo los IDs (ligero y rápido)
        const idsRes = await fetch('https://api.minymol.com/products/public-ids');
        
        if (!idsRes.ok) {
          if (idsRes.status === 429) {
            setLoading(false);
            return;
          }
          throw new Error(`HTTP ${idsRes.status}`);
        }
        
        const idsData = await idsRes.json();

        if (!Array.isArray(idsData)) throw new Error('Respuesta inválida del backend');

        const ids = idsData.map(p => p.product_id);

        // Hacer shuffle de los IDs (orden aleatorio)
        const shuffledIds = shuffleArray(ids);
        setProductIds(shuffledIds);

        // Cargar los primeros productos
        const firstIds = shuffledIds.slice(0, ITEMS_PER_LOAD);
        
        const previewsRes = await fetch('https://api.minymol.com/products/previews', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: firstIds }),
        });
        
        if (!previewsRes.ok) {
          if (previewsRes.status === 429) {
            setLoading(false);
            return;
          }
          throw new Error(`HTTP ${previewsRes.status}`);
        }

        const previews = await previewsRes.json();
        
        setVisibleProducts(previews);
        loadIndexRef.current = ITEMS_PER_LOAD;
        setHasMore(shuffledIds.length > ITEMS_PER_LOAD);
        setLoading(false);
      } catch (error) {
        if (!error.message?.includes('429')) {
          console.error('Error al cargar productos:', error);
        }
        setLoading(false);
      }
    };

    fetchProductos();
  }, []); // Solo se ejecuta al montar el componente

  // Effect adicional que detecta navegación de vuelta a Home
  useEffect(() => {
    // Si venimos de otra página y no tenemos productos visibles, recargar
    if (lastLocationRef.current !== '/' && visibleProducts.length === 0 && productIds.length === 0) {
      console.log('🔄 Volviendo a Home desde otra página');
      setLoading(true);
    }
    lastLocationRef.current = location.pathname;
  }, [location.pathname, visibleProducts.length, productIds.length]);

  // Effect que verifica productos después de un delay
  useEffect(() => {
    const checkProducts = setTimeout(() => {
      if (visibleProducts.length === 0 && !loading && productIds.length === 0) {
        console.log('⚠️ No hay productos visibles, recargando');
        setLoading(true);
      }
    }, 1000);

    return () => clearTimeout(checkProducts);
  }, [loading, visibleProducts.length, productIds.length]);

  useEffect(() => {
    // crea la versión limitada de handleScroll
    const throttledScroll = throttle(handleScroll, 200);

    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      throttledScroll.cancel(); // limpia el throttle
    };
  }, [handleScroll]);

  // Componente condicional para mostrar Reels o AutoCarousel
  const ReelsOrCarousel = () => {
    const [showAutoCarousel, setShowAutoCarousel] = useState(false);
    
    // Verificar si el usuario es proveedor
    const usuario = localStorage.getItem('usuario');
    const isProvider = usuario && JSON.parse(usuario)?.proveedorInfo?.id;

    // Callback que el componente Reels puede usar para indicar que no tiene contenido
    const handleReelsEmpty = useCallback(() => {
      // Solo mostrar AutoCarousel si el usuario NO es proveedor
      if (!isProvider) {
        setShowAutoCarousel(true);
      }
    }, [isProvider]);

    // Si debemos mostrar AutoCarousel (no hay reels y no es proveedor)
    if (showAutoCarousel) {
      return <AutoCarousel />;
    }

    // Siempre mostrar Reels primero (manejará internamente si mostrar contenido o no)
    return <Reels onEmpty={handleReelsEmpty} />;
  };

  return (
    <div className="home-container">
      <EnableNotifications />
      <Header isHome={true} />
      {/* Componente condicional: Reels para proveedores o AutoCarousel para usuarios normales */}
      <ReelsOrCarousel />
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '10vh', width: '100%' }}>
          <ClipLoader color="#fa7e17" size={30} />
        </div>
      ) : (
        <>
          <Masonry
            breakpointCols={breakpointColumnsObj}
            className="products-cont masonry-grid"
            columnClassName="masonry-grid_column"
          >
            {visibleProducts.map((prod, index) => (
              <ProductWrapper key={`wrapper-${index}`} product={prod} index={index} />
            ))}
          </Masonry>
          {loadingMore && hasMore && (
            <div className="loader-bottom" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '10vh', width: '100%' }}>
              <ClipLoader color="#fa7e17" size={25} />
            </div>
          )}
          {!hasMore && (
            <div style={{ textAlign: 'center', color: '#888', padding: '1rem' }}>
              No hay más productos para mostrar
            </div>
          )}
        </>
      )}
      <WhatsAppBtn />
      <NavInf selected="home" />
      <Footer navinf={true} />
    </div>
  );
}

export default Home;
